import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, DownloadIcon } from 'lucide-react';
const FLYER_IMAGE = 'https://cdn.magicpatterns.com/uploads/4aNBYrdJUYsTdtrcjjAdQj/HAA_Voices_of_Impact.jpg';
const COPY = {
  back: 'Back to site',
  eyebrow: 'Voices of Impact · Save the Date',
  title: 'An evening to connect, empower, and celebrate',
  subtitle: 'Join leaders from across the country for a private cocktail reception celebrating the journalists, storytellers, and changemakers whose work is elevating Latino voices and strengthening communities nationwide.',
  dateLabel: 'Date',
  date: 'July 24, 2026',
  locationLabel: 'Location',
  location: 'New Orleans',
  timeLabel: 'Time',
  time: '4:00 PM – 5:30 PM',
  download: 'Download Invitation',
  viewFlyer: 'View full invitation'
};
export function SaveTheDatePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const t = COPY;
  return <div className="min-h-screen bg-gradient-to-br from-haa-navy via-haa-blue to-haa-midblue text-white font-nunito relative overflow-hidden" data-id="element-4032" data-mp="32ysr41r2v877">
      {/* Decorative blurs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-haa-skyblue/20 rounded-full blur-3xl pointer-events-none" data-id="element-4033" data-mp="2y2aqne2s5w0o" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-haa-blue/30 rounded-full blur-3xl pointer-events-none" data-id="element-4034" data-mp="2ozjetd5zsy8i" />
      <div className="absolute top-1/3 right-0 w-80 h-80 bg-haa-midblue/20 rounded-full blur-3xl pointer-events-none" data-id="element-4035" data-mp="3lpsdcv76u3ws" />

      {/* Header */}
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between border-b border-white/10 backdrop-blur-sm" data-id="element-4036" data-mp="3hoqjyuzycm2q">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association — Home" data-id="element-4037" data-mp="37ynmahhvyjy2">
          <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-4038" data-mp="99ggbigez8su" />
        </a>
        <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-4039" data-mp="seuwaj17od9y">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-4040" data-mp="ikkukaqdq2mk" />
          {t.back}
        </a>
      </header>

      {/* Main content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-20" data-id="element-4041" data-mp="9lzhgv3offt6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-4042" data-mp="3rs5d2mn1zs8z">
          {/* Flyer "popup" card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="order-2 lg:order-1 flex justify-center" data-id="element-4043" data-mp="3vsxo9lfhk77w">
            <div className="relative w-full max-w-md" data-id="element-4044" data-mp="3ajunp3805c5y">
              {/* Soft glow */}
              <div className="absolute -inset-4 bg-haa-skyblue/30 rounded-3xl blur-2xl pointer-events-none" data-id="element-4045" data-mp="3oazsejq88un5" />
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="relative block rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 bg-white group" aria-label={t.viewFlyer} data-id="element-4046" data-mp="3jig5gw5ngn5o">
                <img src={FLYER_IMAGE} alt={t.title} className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-4047" data-mp="b24rjhcx6rf4" />
              </a>
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-white text-haa-navy font-bold text-sm hover:bg-haa-skyblue hover:text-haa-navy transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-4048" data-mp="2cwm97s6f53dg">
                <DownloadIcon className="w-4 h-4" data-id="element-4049" data-mp="2j8ylpx8un8cl" />
                {t.download}
              </a>
            </div>
          </motion.div>

          {/* Details */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.15
        }} className="order-1 lg:order-2" data-id="element-4050" data-mp="1qnf5t2daxkra">
            <span className="inline-block font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-bold" data-id="element-4051" data-mp="17wo1if868kjr">
              {t.eyebrow}
            </span>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-wider leading-[0.9] mt-3 text-white" data-id="element-4052" data-mp="1csml7pik1edu">
              {t.title}
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl" data-id="element-4053" data-mp="10dohq8gssqzv">
              {t.subtitle}
            </p>

            {/* Detail rows */}
            <div className="mt-10 space-y-4 max-w-xl" data-id="element-4054" data-mp="qefsfswvo0ju">
              <DetailRow icon={<CalendarIcon className="w-5 h-5" data-id="element-4056" data-mp="mclu3ra5n8mu" />} label={t.dateLabel} value={t.date} data-id="element-4055" data-mp="qdnz6oqr6ewq" />
              <DetailRow icon={<MapPinIcon className="w-5 h-5" data-id="element-4058" data-mp="hkkmiinxm635" />} label={t.locationLabel} value={t.location} data-id="element-4057" data-mp="hllvmhle0ii8" />
              <DetailRow icon={<ClockIcon className="w-5 h-5" data-id="element-4060" data-mp="7oqzu6gi0w7n" />} label={t.timeLabel} value={t.time} data-id="element-4059" data-mp="7ny21uziph1n" />
            </div>

            {/* Actions */}
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-4061" data-mp="3urvtusqko1rg">
              <a href={FLYER_IMAGE} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-white text-haa-navy font-bold text-base hover:bg-haa-skyblue transition-all duration-200 shadow-lg hover:shadow-xl" data-id="element-4062" data-mp="3k7a9pqm41dbm">
                {t.viewFlyer}
              </a>
              <a href="#" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold text-base transition-all duration-200" data-id="element-4063" data-mp="7q5alq7bogvv">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-4064" data-mp="3u57xye3yza48" />
                {t.back}
              </a>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}
function DetailRow({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15" data-id="element-4065" data-mp="vgsnkpw3hwpa">
      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-white/15 flex items-center justify-center text-haa-skyblue" data-id="element-4066" data-mp="14rx2lkdvwuk2">
        {icon}
      </div>
      <div data-id="element-4067" data-mp="1ej1gshvqkbdu">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold" data-id="element-4068" data-mp="1eirrkwiim897">
          {label}
        </p>
        <p className="text-lg font-bold text-white mt-0.5" data-id="element-4069" data-mp="19lrsx6nzw7s4">{value}</p>
      </div>
    </div>;
}